import React, { useState, useCallback, useEffect } from "react";

/**
 * XRAi digital – Patient health screening report (2 × A4)
 *
 * Usage:
 *   <PatientReport patientId={236477} logoSrc="/campanylogo.jpeg" headers={{ Authorization: `Token ${token}` }} autoLoad hideControls />
 *
 * Props
 *   patientId     number|string  Patient to load (default 236477)
 *   apiBase       string         API root (default https://cbackend.xraidigital.com)
 *   headers       object         Extra request headers, e.g. auth token
 *   credentials   string         fetch credentials mode, e.g. "include" for cookie auth
 *   logoSrc       string         Logo path (put campanylogo.jpeg in your public folder)
 *   fillMissing   boolean        Fill empty body-composition fields with estimated/sample values
 *                                (always labelled in the report). Default true.
 *   campName      string         Text shown under the title (default "DC Camp")
 *   autoLoad      boolean        Load the report as soon as the component mounts. Default false.
 *   hideControls  boolean        Hide the built-in Generate / Print buttons (use your own). Default false.
 */

const BRAND = "#11a8a4";
const RED = "#e8777e", GREEN = "#5cc98b", AMBER = "#f0c261";

/* ---------- helpers ---------- */
const num = (v) => (v === null || v === undefined || v === "" || isNaN(Number(v)) ? null : Number(v));
const show = (v, unit = "", d = 1) => (v === null ? "—" : `${Number.isInteger(v) ? v : v.toFixed(d)}${unit}`);
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const isYes = (v) => String(v || "").toLowerCase() === "yes";
const initials = (n = "") => n.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "P";
const isPositive = (v) => String(v || "").toUpperCase() === "POSITIVE";

function bmiStatus(b) {
  if (b === null) return ["na", "No data"];
  if (b < 18.5) return ["bad", "Underweight"];
  if (b < 25) return ["ok", "Normal"];
  if (b < 30) return ["warn", "Overweight"];
  return ["bad", "Obese"];
}
function rangeStatus(v, [lo, hi]) {
  if (v === null) return ["na", "No data"];
  if (v < lo) return ["bad", "Low"];
  if (v > hi) return ["warn", "High"];
  return ["ok", "Normal"];
}

/* ---------- small UI parts ---------- */
const Chip = ({ s }) => <span className={`xr-chip xr-${s[0]}`}>{s[1]}</span>;

function Ring({ label, value, unit, pct, color, status }) {
  return (
    <div className="xr-kpi" style={{ "--c": color }}>
      <div className="xr-ring" style={{ "--p": clamp(pct, 0, 100) }}><b>{value}</b></div>
      <div>
        <small>{label}</small>
        <div className="xr-u">{unit}</div>
        <Chip s={status} />
      </div>
    </div>
  );
}

function Gauge({ title, val, unit, min, max, zones, status }) {
  if (val === null) return null;
  const pct = (x) => clamp(((x - min) / (max - min)) * 100, 0, 100);
  let prev = 0;
  const stops = zones.map(([end, col]) => {
    const e = pct(end);
    const s = `${col} ${prev}% ${e}%`;
    prev = e;
    return s;
  });
  return (
    <div className="xr-gauge">
      <div className="xr-gh">
        <b>{title} <span className="xr-mute">{show(val)} {unit}</span></b>
        <Chip s={status} />
      </div>
      <div className="xr-bar" style={{ background: `linear-gradient(90deg,${stops.join(",")})` }}>
        <i style={{ left: `${pct(val)}%` }} />
      </div>
      <div className="xr-scale">{zones.map((z, i) => <span key={i}>{z[2]}</span>)}</div>
    </div>
  );
}

const Bar = ({ p }) => <div className="xr-mb"><i style={{ width: `${clamp(p, 0, 100)}%` }} /></div>;

function Table({ head, rows, className = "" }) {
  return (
    <table className={className}>
      <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{r.map((c, j) => <td key={j} className={j === 0 ? "xr-n" : ""}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- main component ---------- */
export default function PatientReport({
  patientId = 236477,
  apiBase = "https://cbackend.xraidigital.com",
  headers = {},
  credentials,
  logoSrc = "/campanylogo.jpeg",
  fillMissing = true,
  campName = "DC Camp",
  autoLoad = false,
  hideControls = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/api/technician/patients/${patientId}/report-data/`, {
        headers: { Accept: "application/json", ...headers },
        ...(credentials ? { credentials } : {}),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      if (json.success === false) throw new Error(json.message || "The report data could not be loaded.");
      setData(json);
    } catch (e) {
      setData(null);
      setError(e.message || "Something went wrong while loading the report.");
    } finally {
      setLoading(false);
    }
  }, [apiBase, patientId, headers, credentials]);

  // Load the report as soon as it opens (used by the Reviewer page)
  useEffect(() => {
    if (autoLoad) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, autoLoad]);

  return (
    <div className="xr-root">
      <style>{CSS}</style>

      {(!hideControls || loading || error) && (
      <div className="xr-ui">
        {!hideControls && (
          <>
            <button className="xr-btn" onClick={generate} disabled={loading}>
              {loading ? "Generating…" : data ? "Regenerate report" : "Generate report"}
            </button>
            {data && (
              <button className="xr-btn xr-ghost" onClick={() => window.print()}>
                Print / Save as PDF
              </button>
            )}
          </>
        )}
        {hideControls && loading && <p className="xr-hint">Loading report…</p>}
        {error && <div className="xr-error" role="alert">{error}</div>}
        {!hideControls && data && <p className="xr-hint">In the print dialog choose paper size A4, margins None, and turn on Background graphics.</p>}
      </div>
      )}

      {data && <Report data={data} logoSrc={logoSrc} fillMissing={fillMissing} campName={campName} />}
    </div>
  );
}

/* ---------- the two A4 pages ---------- */
function Report({ data, logoSrc, fillMissing, campName }) {
  const p = data.patient || {};
  const v = data.vitals || {};
  const c = data.consultation || {};
  const services = data.services || [];

  const age = num(p.age);
  const female = String(p.gender || "").toLowerCase().startsWith("f");
  const h = num(v.height), w = num(v.weight), fat = num(v.body_fat), hb = num(v.hemoglobin);
  const bmi = num(v.bmi) ?? (h && w ? +(w / ((h / 100) ** 2)).toFixed(2) : null);

  const FAT = female ? [16, 30, 35] : [11, 21, 26];
  const HB = female ? [12, 15.5] : [13.5, 17.5];

  const bmiS = bmiStatus(bmi);
  const fatS = rangeStatus(fat, [FAT[0], FAT[1]]);
  const hbS = rangeStatus(hb, HB);

  const screening = [["HIV", v.hiv], ["HCV", v.hcv], ["HBsAg", v.hbsag]];
  const posCount = screening.filter(([, r]) => isPositive(r)).length;

  /* Estimated values for fields the device did not return */
  const est = {};
  if (w && fat !== null) {
    est.fat_mass = +(w * fat / 100).toFixed(1);
    est.ffm = +(w - est.fat_mass).toFixed(1);
    est.water_w = +(est.ffm * 0.73).toFixed(1);
    est.water_p = +((est.water_w / w) * 100).toFixed(1);
    est.subq = +(fat * 0.82).toFixed(1);
  }
  if (h) est.ibw = +(22 * (h / 100) ** 2).toFixed(1);
  est.visceral = 3;
  if (age) est.body_age = Math.max(age - 2, 1);

  const pick = (apiVal, key, unit, d = 1) => {
    const a = num(apiVal);
    if (a !== null) return { text: show(a, unit, d), tag: ["ok", "Measured"], raw: a };
    if (!fillMissing || est[key] === undefined) return { text: "—", tag: ["na", "Not recorded"], raw: null };
    const sample = ["visceral", "body_age", "subq"].includes(key);
    return { text: show(est[key], unit, d), tag: ["na", sample ? "Sample" : "Estimated"], raw: est[key] };
  };

  const E = {
    fat_mass: pick(v.fat_mass, "fat_mass", " kg"),
    ffm: pick(v.fat_free_body_weight, "ffm", " kg"),
    water_w: pick(v.water_weight, "water_w", " kg"),
    water_p: pick(v.body_water, "water_p", " %"),
    subq: pick(v.subcutaneous_fat, "subq", " %"),
    visc: pick(v.visceral_fat, "visceral", " (rating)", 0),
    bage: pick(v.body_age, "body_age", " years", 0),
    ibw: pick(v.ideal_body_weight, "ibw", " kg"),
  };
  const anyEstimated = Object.values(E).some((x) => x.tag[1] === "Estimated" || x.tag[1] === "Sample");
  const na = ["na", "No range"];

  const measured = [
    ["Weight", show(w, " kg"), bmiS[1] === "Underweight" ? ["bad", "Low"] : na],
    ["Body fat", <>{show(fat, " %")}{fat !== null && <Bar p={fat} />}</>, fatS],
    ["Muscle mass", show(num(v.muscle_mass), " kg"), na],
    ["Muscle rate", <>{show(num(v.muscle_rate), " %")}{num(v.muscle_rate) !== null && <Bar p={num(v.muscle_rate)} />}</>, na],
    ["Skeletal muscle", <>{show(num(v.skeletal_muscle), " %")}{num(v.skeletal_muscle) !== null && <Bar p={num(v.skeletal_muscle)} />}</>, na],
    ["Bone mass", show(num(v.bone_mass), " kg"), na],
    ["Protein mass", show(num(v.protein_mass), " kg"), na],
    ["Protein", <>{show(num(v.protein), " %")}{num(v.protein) !== null && <Bar p={num(v.protein)} />}</>, na],
    ["BMR", show(num(v.bmr), " kcal/day", 0), na],
  ].map(([a, b, s]) => [a, b, <Chip key={a} s={s} />]);

  const estimated = [
    ["Fat mass", E.fat_mass], ["Fat-free body weight", E.ffm], ["Water weight", E.water_w],
    ["Body water", E.water_p], ["Subcutaneous fat", E.subq], ["Visceral fat", E.visc],
    ["Body age", E.bage], ["Ideal body weight", E.ibw],
  ].map(([label, x]) => [label, <>{x.text}{(label === "Body water" || label === "Subcutaneous fat") && x.raw !== null && <Bar p={x.raw} />}</>, <Chip key={label} s={x.tag} />]);

  const consultation = [
    ["Chief Complaint", c.chief_complaint ? `“${c.chief_complaint}”` : "—"],
    ["History", c.history || "—"],
    ["Allergies", isYes(c.has_allergies) ? c.allergies || "Yes" : "None"],
    ["Medical Conditions", isYes(c.has_medical_conditions) ? c.medical_conditions || "Yes" : "None"],
    ["Medications", isYes(c.has_medications) ? c.medications || "Yes" : "None"],
    ["Diagnostic Tests", c.diagnostic_tests || "—"],
    ["Advice", c.advice || "—"],
  ].map(([a, b], i) => [<><span className="xr-k">{i + 1}</span>{a}</>, b]);

  // Get the screening date from the patient data
  const screeningDate = p.screening_date || p.test_date;

  // Format the date as "28 October 2026"
  const formattedScreeningDate = screeningDate
    ? new Date(`${screeningDate}T00:00:00`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Screening date not available";

  // Report header
  const header = () => (
    <div className="xr-hd">
      <img
        src={logoSrc}
        alt="XRAi digital – accurate imaging, personalized care"
      />

      <div className="xr-r">
        <b>Health Screening Report</b>
        <span>Screening Date: {formattedScreeningDate}</span>
      </div>
    </div>
  );

  return (
    <div className="xr-wrap">
      <div className="xr-sheets">
        {/* ---------------- Page 1 ---------------- */}
        <div className="xr-sheet">
          {header()}
          <h2>Patient demographics</h2>
          <div className="xr-id">
            <div className="xr-av">{initials(p.patient_name)}</div>
            <div>
              <div className="xr-nm">{p.patient_name || "—"}</div>
              <div className="xr-sb">{age ?? "—"} years · {p.gender || "—"} · Patient ID {p.id ?? "—"}</div>
            </div>
          </div>
          <div className="xr-demo">
            {[
              ["Patient name", p.patient_name || "—"], ["Patient ID", p.id ?? "—"],
              ["Patient unique ID", p.patient_unique_id || "Not recorded"], [
                "Screening Date",
                p.screening_date
                  ? new Date(`${p.screening_date}T00:00:00`).toLocaleDateString(
                      "en-GB",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    )
                  : p.test_date
                    ? new Date(`${p.test_date}T00:00:00`).toLocaleDateString(
                        "en-GB",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }
                      )
                    : "—",
              ],
              ["Age", age !== null ? `${age} years` : "—"], ["Gender", p.gender || "—"],
              ["Height", show(h, " cm")], ["Weight", show(w, " kg")],
            ].map(([a, b]) => <div key={a}><small>{a}</small><b>{b}</b></div>)}
          </div>

          <div className="xr-kpis">
            <Ring label="BMI" value={show(bmi, "", 2)} unit="kg/m²" pct={((bmi || 0) / 40) * 100} color={`var(--${bmiS[0]})`} status={bmiS} />
            <Ring label="Body fat" value={show(fat)} unit="percent" pct={((fat || 0) / 40) * 100} color={`var(--${fatS[0]})`} status={fatS} />
            <Ring label="Hemoglobin" value={show(hb)} unit="g/dL" pct={((hb || 0) / 20) * 100} color={`var(--${hbS[0]})`} status={hbS} />
            <Ring label="Screening" value={`${posCount}/3`} unit="tests positive" pct={(posCount / 3) * 100}
              color={posCount ? "var(--bad)" : "var(--ok)"}
              status={posCount ? ["bad", posCount === 3 ? "HIV·HCV·HBsAg" : "Positive found"] : ["ok", "All negative"]} />
          </div>

          <h2>Body measurements at a glance</h2>
          <Gauge title="BMI" val={bmi} unit="kg/m²" min={10} max={40} status={bmiS}
            zones={[[18.5, RED, "Under 18.5"], [25, GREEN, "18.5–24.9"], [30, AMBER, "25–29.9"], [40, RED, "30+"]]} />
          <Gauge title="Body fat" val={fat} unit="%" min={0} max={40} status={fatS}
            zones={[[FAT[0], AMBER, `Under ${FAT[0]}`], [FAT[1], GREEN, `${FAT[0]}–${FAT[1]}`], [FAT[2], AMBER, `${FAT[1]}–${FAT[2]}`], [40, RED, `${FAT[2]}+`]]} />
          <Gauge title="Hemoglobin" val={hb} unit="g/dL" min={8} max={20} status={hbS}
            zones={[[HB[0], RED, `Under ${HB[0]}`], [HB[1], GREEN, `${HB[0]}–${HB[1]}`], [20, AMBER, `${HB[1]}+`]]} />

          <h2>Body composition</h2>
          <div className="xr-two">
            <Table head={["Measured", "Value", "Status"]} rows={measured} />
            <Table head={["Estimated *", "Value", "Status"]} rows={estimated} />
          </div>
          {anyEstimated && (
            <div className="xr-fn">
              * Values marked Estimated or Sample were not measured by the device. Fat mass, fat-free weight, water and ideal weight
              are calculated from the recorded weight, height and body fat.
            </div>
          )}
          <div className="xr-ft"><span>XRAi digital · Accurate imaging | Personalized care</span><span>Page 1 of 2</span></div>
        </div>

        {/* ---------------- Page 2 ---------------- */}
        <div className="xr-sheet">
          {header()}
          <div className="xr-pt">
            <span>Patient name: <b>{p.patient_name || "—"}</b></span>
            <span>Patient ID: <b>{p.id ?? "—"}</b></span>
            <span>Age / Gender: <b>{age ?? "—"} / {p.gender || "—"}</b></span>
          </div>

          <h2>Laboratory and screening</h2>
          <Table
            head={["Test", "Result", "Status", "Action"]}
            rows={[
              ["Hemoglobin", hb !== null ? `${show(hb)} g/dL` : "—", <Chip key="hb" s={hbS} />, `Reference ${HB[0]}–${HB[1]} g/dL`],
              ...screening.map(([name, r]) => [
                name,
                r ? String(r).toUpperCase() : "—",
                <Chip key={name} s={isPositive(r) ? ["bad", "Positive"] : r ? ["ok", "Negative"] : ["na", "Not tested"]} />,
                isPositive(r) ? (name === "HIV" ? "Confirmatory test and counselling" : "Confirmatory test and specialist referral") : "—",
              ]),
            ]}
          />
          {posCount > 0 && (
            <div className="xr-alert">
              <b>Confidential.</b> {screening.filter(([, r]) => isPositive(r)).map(([n]) => n).join(", ")} reported as positive.
              Share only with authorised clinical staff and the patient, and follow pre- and post-test counselling.
              Confirmatory testing is needed.
            </div>
          )}

          <h2>Doctor consultation</h2>
          <Table head={["Item", "Details"]} rows={consultation} className="xr-cn" />

          <h2>Services completed</h2>
          <ul className="xr-tl">
            {services.length === 0 && <li><small>No services recorded.</small></li>}
            {services.map((s, i) => (
              <li key={i}>
                <b>{s.service_name}</b>{" "}
                <Chip s={s.is_completed || String(s.status).toLowerCase() === "done" ? ["ok", "Done"] : ["warn", s.status || "Pending"]} />
              </li>
            ))}
          </ul>

          <div className="xr-ft"><span>This report is not a diagnosis. A qualified doctor should review the findings.</span><span>Page 2 of 2</span></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- styles (scoped under .xr-root) ---------- */
const CSS = `
.xr-root,.xr-sheets{--brand:${BRAND};--brand-d:#0b7f7c;--brand-t:#e3f6f5;--ink:#12292a;--mute:#5b7273;--line:#d7e5e5;--ok:#1f9d57;--ok-t:#e1f5e9;--warn:#b7791f;--warn-t:#fdf1d8;--bad:#d33f49;--bad-t:#fde6e8;--na:#6b7f80;--na-t:#e9f0f0;font:12.5px/1.45 "Segoe UI",system-ui,-apple-system,Roboto,Arial,sans-serif;color:var(--ink)}
.xr-root *,.xr-sheets *{box-sizing:border-box}
.xr-ui{display:flex;flex-wrap:wrap;gap:10px;align-items:center;padding:12px 0}
.xr-btn{background:var(--brand);color:#fff;border:0;border-radius:8px;padding:10px 20px;font-size:14px;font-weight:600;cursor:pointer}
.xr-btn:hover{background:var(--brand-d)}.xr-btn:disabled{opacity:.6;cursor:wait}
.xr-btn:focus-visible{outline:3px solid #0b7f7c;outline-offset:2px}
.xr-ghost{background:#fff;color:var(--brand-d);border:1.5px solid var(--brand)}.xr-ghost:hover{background:var(--brand-t)}
.xr-error{width:100%;background:var(--bad-t);color:var(--bad);border-left:4px solid var(--bad);border-radius:6px;padding:8px 12px;font-size:13px}
.xr-hint{width:100%;margin:0;color:var(--mute);font-size:12px}
.xr-wrap{overflow-x:auto;padding:6px 0 14px}
.xr-sheets{width:210mm;margin:0 auto}
.xr-sheet{width:210mm;height:297mm;padding:11mm 12mm 0;position:relative;overflow:hidden;background:#fff;color:var(--ink);margin:0 0 14px;box-shadow:0 4px 18px rgba(0,0,0,.18)}
.xr-hd{display:flex;justify-content:space-between;align-items:center;padding-bottom:9px;border-bottom:3px solid var(--brand)}
.xr-hd img{height:15mm;display:block}
.xr-r{text-align:right}.xr-r b{font-size:17px;color:var(--brand-d);display:block}.xr-r span{color:var(--mute);font-size:11.5px}
.xr-sheets h2{margin:14px 0 8px;font-size:14px;font-weight:700;color:var(--brand-d);display:flex;align-items:center;gap:7px}
.xr-sheets h2::before{content:"";width:4px;height:15px;border-radius:2px;background:var(--brand)}
.xr-id{display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;background:var(--brand-t);margin-bottom:10px}
.xr-av{flex:none;width:48px;height:48px;border-radius:50%;background:linear-gradient(135deg,var(--brand),var(--brand-d));color:#fff;font-weight:700;font-size:17px;display:grid;place-items:center;box-shadow:0 0 0 2px #fff,0 0 0 4px var(--brand)}
.xr-nm{font-size:18px;font-weight:700}.xr-sb{color:var(--mute);font-size:11.5px}
.xr-demo{display:grid;grid-template-columns:repeat(4,1fr);gap:8px 14px}
.xr-demo div{border-bottom:1px dashed var(--line);padding-bottom:4px}
.xr-demo small{display:block;color:var(--mute);font-size:10.5px}.xr-demo b{font-size:12.5px}
.xr-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-top:12px}
.xr-kpi{display:flex;align-items:center;gap:8px;border:1px solid var(--line);border-top:3px solid var(--c);border-radius:9px;padding:8px}
.xr-kpi small{color:var(--mute);font-size:10.5px}.xr-u{font-size:10px;color:var(--mute);margin:0 0 4px}
.xr-ring{flex:none;width:52px;height:52px;border-radius:50%;background:conic-gradient(var(--c) calc(var(--p)*1%),var(--line) 0);display:grid;place-items:center;position:relative}
.xr-ring::before{content:"";position:absolute;inset:5px;border-radius:50%;background:#fff}
.xr-ring b{position:relative;font-size:12px}
.xr-chip{display:inline-block;padding:1px 8px;border-radius:99px;font-size:10.5px;font-weight:700;white-space:nowrap}
.xr-ok{background:var(--ok-t);color:var(--ok)}.xr-bad{background:var(--bad-t);color:var(--bad)}.xr-warn{background:var(--warn-t);color:var(--warn)}.xr-na{background:var(--na-t);color:var(--na)}
.xr-gauge{margin:0 0 15px}.xr-gh{display:flex;justify-content:space-between;align-items:center;margin-bottom:5px}
.xr-mute{color:var(--mute);font-weight:400}
.xr-bar{position:relative;height:10px;border-radius:99px}
.xr-bar i{position:absolute;top:-4px;width:4px;height:18px;background:var(--ink);border-radius:2px;transform:translateX(-50%);box-shadow:0 0 0 2px #fff}
.xr-scale{display:flex;justify-content:space-between;font-size:10px;color:var(--mute);margin-top:4px}
.xr-sheets table{width:100%;border-collapse:collapse;font-size:12px}
.xr-sheets th{background:var(--brand-t);color:var(--brand-d);text-align:left;padding:5px 8px;font-weight:700}
.xr-sheets td{padding:5px 8px;border-bottom:1px solid var(--line);vertical-align:middle}
.xr-sheets tbody tr:nth-child(even) td{background:rgba(17,168,164,.05)}
.xr-n{font-weight:600}
.xr-mb{height:4px;border-radius:2px;background:var(--line);margin-top:3px;min-width:60px}.xr-mb i{display:block;height:100%;border-radius:2px;background:var(--brand)}
.xr-two{display:grid;grid-template-columns:1fr 1fr;gap:10px;align-items:start}
.xr-two table{font-size:11.5px}.xr-two td,.xr-two th{padding:4px 6px}
.xr-fn{font-size:10px;color:var(--mute);margin-top:6px}
.xr-pt{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:10px;padding:8px 12px;border-radius:8px;background:var(--brand-t);font-size:12.5px}
.xr-pt b{color:var(--brand-d)}
.xr-cn td{padding:9px 10px;font-size:12.5px}.xr-cn td:first-child{width:36%}
.xr-k{display:inline-grid;place-items:center;width:18px;height:18px;border-radius:50%;background:var(--brand);color:#fff;font-size:10.5px;margin-right:8px}
.xr-alert{border-left:4px solid var(--bad);background:var(--bad-t);border-radius:6px;padding:7px 11px;margin-top:8px;font-size:11.5px}
.xr-tl{list-style:none;margin:0;padding:0 0 0 14px;border-left:3px solid var(--brand)}
.xr-tl li{position:relative;padding:0 0 9px 12px}
.xr-tl li::before{content:"";position:absolute;left:-21px;top:3px;width:11px;height:11px;border-radius:50%;background:var(--brand);border:2px solid #fff}
.xr-tl small{color:var(--mute)}
.xr-ft{position:absolute;left:12mm;right:12mm;bottom:7mm;display:flex;justify-content:space-between;border-top:1px solid var(--line);padding-top:5px;font-size:10px;color:var(--mute)}
@media print{
  @page{size:A4;margin:0}
  *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  body *{visibility:hidden}
  .xr-sheets,.xr-sheets *{visibility:visible}
  .xr-sheets{position:absolute;left:0;top:0;margin:0}
  .xr-sheet{margin:0;box-shadow:none;break-after:page}
  .xr-sheet:last-child{break-after:auto}
}
`;